import { useEffect, useRef, useState } from 'react';
import {
  AlignCenterIcon,
  AlignLeftIcon,
  AlignRightIcon,
  BoldIcon,
  Button,
  ClearFormatIcon,
  HighlightIcon,
  IconButton,
  IndentIcon,
  ItalicIcon,
  ListBulletIcon,
  ListOrderedIcon,
  OutdentIcon,
  PlusIcon,
  Popover,
  SettingsIcon,
  StrikethroughIcon,
  TextColorIcon,
  TrashIcon,
  UnderlineIcon,
  cx,
} from '@/shared/ui';
import { PAPER_COLORS, type Note, type PaperColor, type WorkpadPrefs } from '@/features/content/services/notesService';
import { formatCueTime } from '@/features/content/utils/vttCues';
import { sanitizeNoteHtml } from '@/features/content/utils/sanitizeNoteHtml';

/* document.execCommand is deprecated but is still the only way to get rich-text
   editing out of a contenteditable without shipping an editor framework. Every
   browser we target implements it, and the alternative — ProseMirror, Lexical,
   TipTap — is a dependency and a data model this feature has not earned yet.
   Revisit if the workpad grows beyond inline formatting and lists. */
function exec(command: string, value?: string) {
  document.execCommand(command, false, value);
}

const TEXT_COLORS = ['#1a1a1a', '#0f9b70', '#d9694f', '#4a86c8', '#9b5bb8'];
const HIGHLIGHTS = ['#fff3a3', '#c8f0dd', '#ffd9d1', '#d7e6f7', '#ecd9f5'];

const PAPER_LABELS: Record<PaperColor, string> = {
  white: 'White',
  pink: 'Pink',
  olive: 'Olive',
  mint: 'Mint',
  blue: 'Blue',
  purple: 'Purple',
};

export type WorkpadProps = {
  notes: Note[];
  prefs: WorkpadPrefs;
  /** Where the player is, so a new note is pinned to the right moment. */
  currentTime: number;
  onAddNote: (timestamp: number, html: string) => void;
  onUpdateNote: (noteId: string, html: string) => void;
  onDeleteNote: (noteId: string) => void;
  onPrefsChange: (prefs: Partial<WorkpadPrefs>) => void;
  onSeek: (seconds: number) => void;
};

export function Workpad({
  notes,
  prefs,
  currentTime,
  onAddNote,
  onUpdateNote,
  onDeleteNote,
  onPrefsChange,
  onSeek,
}: WorkpadProps) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsAnchor = useRef<HTMLButtonElement>(null);
  /* The moment the draft was opened, which is also what "drafting" means — one
     piece of state, so the timestamp and the open/closed flag cannot disagree.
     It used to read the live `currentTime` prop at commit time instead, and
     ContentPage pushes that in about four times a second: a note that took forty
     seconds to write was pinned forty seconds past the thing it was about, and
     the label on the open draft ticked upward while you typed. */
  const [draftAt, setDraftAt] = useState<number | null>(null);
  const drafting = draftAt !== null;
  const draftRef = useRef<HTMLDivElement>(null);

  const openDraft = () => setDraftAt(currentTime);
  const closeDraft = () => setDraftAt(null);

  const commitDraft = () => {
    const html = sanitizeNoteHtml(draftRef.current?.innerHTML ?? '').trim();
    // An empty note is a mis-click, not a note.
    if (html && html !== '<br>' && draftAt !== null) onAddNote(draftAt, html);
    if (draftRef.current) draftRef.current.innerHTML = '';
    closeDraft();
  };

  /* React honours `autoFocus` only on button/input/select/textarea; on a div it
     is dropped without a warning, so the draft opened with no caret in it. The
     follow-on was worse than a missed caret: the draft commits on blur, and an
     element that never took focus never blurs, so an abandoned draft sat there
     with the "Add a note" button hidden behind it and no way out but Escape. */
  useEffect(() => {
    if (drafting) draftRef.current?.focus();
  }, [drafting]);

  return (
    <div className={cx('workpad', `workpad--${prefs.paper}`, prefs.lines && 'workpad--ruled')}>
      {/* Every control below acts on the caret in the note, so it must not take
          focus away from it. mousedown is where focus moves — before click —
          and letting it happen fires the note's onBlur, which commits the note
          and, for a draft, unmounts it; execCommand then runs with no selection
          in any editable host and silently does nothing. Suppressing the
          default on mousedown leaves the caret where it was; the click still
          fires. The settings control is exempt: it opens a popover rather than
          formatting text, and it should take focus like any other button. */}
      <div
        className="workpad__toolbar"
        role="toolbar"
        aria-label="Formatting"
        onMouseDown={(event) => {
          if ((event.target as HTMLElement).closest('.workpad__toolbar-end')) return;
          event.preventDefault();
        }}
      >
        <IconButton label="Bold" size="sm" variant="ghost" onClick={() => exec('bold')}>
          <BoldIcon size={15} />
        </IconButton>
        <IconButton label="Italic" size="sm" variant="ghost" onClick={() => exec('italic')}>
          <ItalicIcon size={15} />
        </IconButton>
        <IconButton label="Underline" size="sm" variant="ghost" onClick={() => exec('underline')}>
          <UnderlineIcon size={15} />
        </IconButton>
        <IconButton
          label="Strikethrough"
          size="sm"
          variant="ghost"
          onClick={() => exec('strikeThrough')}
        >
          <StrikethroughIcon size={15} />
        </IconButton>

        <span className="workpad__divider" aria-hidden />

        {/* `role="group"`, not a <label>. A <button> is a labelable element, so
            wrapping these in a <label> implicitly bound it to the first swatch:
            clicking the icon, or the group's own name, forwarded the click to
            that button and recoloured the user's selection with whatever colour
            happened to be first in the list. Neither the icon nor the name is
            interactive content, so nothing suppressed the forwarding. */}
        <div className="workpad__swatch-group" role="group" aria-label="Text colour">
          <TextColorIcon size={15} aria-hidden />
          {TEXT_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              aria-label={`Text colour ${color}`}
              className="workpad__swatch"
              style={{ background: color }}
              onClick={() => exec('foreColor', color)}
            />
          ))}
        </div>

        <div className="workpad__swatch-group" role="group" aria-label="Highlight">
          <HighlightIcon size={15} aria-hidden />
          {HIGHLIGHTS.map((color) => (
            <button
              key={color}
              type="button"
              aria-label={`Highlight ${color}`}
              className="workpad__swatch"
              style={{ background: color }}
              onClick={() => exec('hiliteColor', color)}
            />
          ))}
        </div>

        <span className="workpad__divider" aria-hidden />

        <IconButton
          label="Numbered list"
          size="sm"
          variant="ghost"
          onClick={() => exec('insertOrderedList')}
        >
          <ListOrderedIcon size={15} />
        </IconButton>
        <IconButton
          label="Bulleted list"
          size="sm"
          variant="ghost"
          onClick={() => exec('insertUnorderedList')}
        >
          <ListBulletIcon size={15} />
        </IconButton>
        <IconButton label="Outdent" size="sm" variant="ghost" onClick={() => exec('outdent')}>
          <OutdentIcon size={15} />
        </IconButton>
        <IconButton label="Indent" size="sm" variant="ghost" onClick={() => exec('indent')}>
          <IndentIcon size={15} />
        </IconButton>

        <span className="workpad__divider" aria-hidden />

        <IconButton label="Align left" size="sm" variant="ghost" onClick={() => exec('justifyLeft')}>
          <AlignLeftIcon size={15} />
        </IconButton>
        <IconButton
          label="Align centre"
          size="sm"
          variant="ghost"
          onClick={() => exec('justifyCenter')}
        >
          <AlignCenterIcon size={15} />
        </IconButton>
        <IconButton
          label="Align right"
          size="sm"
          variant="ghost"
          onClick={() => exec('justifyRight')}
        >
          <AlignRightIcon size={15} />
        </IconButton>
        <IconButton
          label="Clear formatting"
          size="sm"
          variant="ghost"
          onClick={() => exec('removeFormat')}
        >
          <ClearFormatIcon size={15} />
        </IconButton>

        <span className="workpad__toolbar-end">
          <IconButton
            ref={settingsAnchor}
            label="Workpad settings"
            size="sm"
            variant="ghost"
            aria-expanded={settingsOpen}
            onClick={() => setSettingsOpen((value) => !value)}
          >
            <SettingsIcon size={15} />
          </IconButton>

          <Popover
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
            anchorRef={settingsAnchor}
            placement="bottom-end"
            label="Workpad settings"
            className="workpad-settings"
          >
            <h4 className="workpad-settings__title">Workpad settings</h4>

            <div className="workpad-settings__row">
              <span className="workpad-settings__label">Workpad colour</span>
              <div className="workpad-settings__swatches">
                {PAPER_COLORS.map((paper) => (
                  <button
                    key={paper}
                    type="button"
                    aria-label={PAPER_LABELS[paper]}
                    aria-pressed={prefs.paper === paper}
                    className={cx(
                      'workpad-settings__paper',
                      `workpad-settings__paper--${paper}`,
                      prefs.paper === paper && 'is-active',
                    )}
                    onClick={() => onPrefsChange({ paper })}
                  />
                ))}
              </div>
            </div>

            {/* `aria-label` and `aria-pressed` on both, as the swatches above
                already have. The visible text is the *state* — "On" / "Off" —
                and the setting it belongs to is a sibling span, which nothing
                associates with the button: a screen reader reached these two in
                sequence and read "On, button" twice, with no way to tell which
                was the rules and which the timestamps, or that either was a
                toggle rather than something that would take you elsewhere. */}
            <div className="workpad-settings__row">
              <span className="workpad-settings__label" id="workpad-timestamps-label">
                Timestamps
              </span>
              <Button
                size="sm"
                aria-labelledby="workpad-timestamps-label"
                aria-pressed={prefs.timestamps}
                onClick={() => onPrefsChange({ timestamps: !prefs.timestamps })}
              >
                {prefs.timestamps ? 'On' : 'Off'}
              </Button>
            </div>

            <div className="workpad-settings__row">
              <span className="workpad-settings__label" id="workpad-lines-label">
                Ruled lines
              </span>
              <Button
                size="sm"
                aria-labelledby="workpad-lines-label"
                aria-pressed={prefs.lines}
                onClick={() => onPrefsChange({ lines: !prefs.lines })}
              >
                {prefs.lines ? 'On' : 'Off'}
              </Button>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => onPrefsChange({ paper: 'white', lines: true, timestamps: false })}
            >
              Reset to default
            </Button>
          </Popover>
        </span>
      </div>

      <div className={cx('workpad__sheet', prefs.timestamps && 'workpad__sheet--stamped')}>
        {notes.map((note) => (
          <article className="note" key={note.id}>
            <button
              type="button"
              className="note__time"
              aria-label={`Jump to ${formatCueTime(note.timestamp)}`}
              onClick={() => onSeek(note.timestamp)}
            >
              {formatCueTime(note.timestamp)}
            </button>

            <div
              className="note__body"
              contentEditable
              suppressContentEditableWarning
              role="textbox"
              aria-label={`Note at ${formatCueTime(note.timestamp)}`}
              /* Sanitised on the way in as well as on the way out. Notes are
                 local today, so the stored HTML is trusted by construction —
                 but this is the only `dangerouslySetInnerHTML` in the app, and
                 the moment notesService becomes an endpoint the store stops
                 being something only this browser has written to. Cleaning on
                 read means already-stored notes are covered too, not just ones
                 written after this landed. */
              dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(note.html) }}
              onBlur={(event) =>
                onUpdateNote(note.id, sanitizeNoteHtml(event.currentTarget.innerHTML))
              }
            />

            <IconButton
              label={`Delete note at ${formatCueTime(note.timestamp)}`}
              size="sm"
              variant="ghost"
              className="note__delete"
              onClick={() => onDeleteNote(note.id)}
            >
              <TrashIcon size={14} />
            </IconButton>
          </article>
        ))}

        {drafting ? (
          <article className="note note--draft">
            {/* The moment the draft was opened — fixed, unlike the button below. */}
            <span className="note__time note__time--static">{formatCueTime(draftAt)}</span>
            <div
              ref={draftRef}
              className="note__body"
              contentEditable
              suppressContentEditableWarning
              role="textbox"
              aria-label="New note"
              onBlur={commitDraft}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  if (draftRef.current) draftRef.current.innerHTML = '';
                  closeDraft();
                }
              }}
            />
          </article>
        ) : (
          // This one *should* track the player: it is advertising where a note
          // opened right now would land.
          <Button variant="ghost" size="sm" iconLeft={<PlusIcon size={15} />} onClick={openDraft}>
            {/* The timestamp takes the numeric face explicitly. It is the one
                place a clock is drawn inside a sentence rather than on its own,
                and inheriting the UI font gave it Atkinson's slashed zero — a
                different `0:00` from the player clock directly above it. */}
            Add a note at <span className="numeric">{formatCueTime(currentTime)}</span>
          </Button>
        )}
      </div>
    </div>
  );
}
